/**
 * @fileoverview Base class for all searchers.
 * Provides shared functionality for UI, input handling, result display, and state.
 * @module searchers/Searcher
 */


// CLAUDE NOTE: I am using a new structure called searchPack which holds a query string, an possible additional values like isEnterKey, flagUserInitiatedThisSearch


import { Lister } from '../results/Lister.js';
import { updateElementVisibilityBasedOnInputElementValue, clearQueuedDelayedSearchMessages, resetSearchPackToOriginalBlankSearch, createItemCopy } from '../utils/jrfuncs.js';
import { sortDataByField, sortDataByFieldAddressSpecial } from '../utils/nynfuncs.js';
import { distanceBetweenTwoItems } from '../utils/jrgeofuncs.js';
import { DEBUG_CBM, MAPAPP_DEFAULTS, AUTO_ZOOM_STYLE, RESULT_MANAGER_CONSTANTS, PTYPES, SEARCH_TYPES, MAP_CONSTANTS, DEBUG_REFRESH } from '../constants.js';
import { setupTextSearchInput } from '../utils/InputHandlerFactory.js';
import { HtmlBuilder } from '../utils/HtmlBuilder.js';
import { ActionDispatcher, DEFAULT_ACTION_SELECTORS } from '../utils/ActionDispatcher.js';

/**
 * Base class for all searchers.
 *
 * Provides shared functionality for:
 * - UI rendering and input handling
 * - Lister integration
 * - State persistence via StateManager
 * - Common event handling
 *
 * Extended by:
 * - DbSearcher (for database searches)
 * - IntSearcher (for local data storage)
 *
 * @class
 */
export class Searcher {
    /**
     * @param {Object} mapApp - The MapApp instance
     * @param {Object} config - Configuration options
     * @param {string} [config.searcherName] - Base name for deriving IDs (see SEARCH_TYPES)
     * @param {string} [config.containerId] - ID of the container element (default: `${searcherName}-lister`)
     * @param {string} [config.inputId] - ID of the search/filter input element (default: `${searcherName}-search-input`)
     * @param {string} [config.clearButtonId] - ID of the clear button element (default: `${searcherName}-search-clear`)
     * @param {string} [config.mapLayerName] - Name of the map layer for results (default: searcherName)
     * @param {string} [config.emptyMessage] - Message when no results
     * @param {Object} [config.listerOptions] - Options to pass to Lister
     */
    constructor(mapApp, config = {}) {
        // MapApp reference - used to access stateManager and other app services
        this.mapApp = mapApp;

        // new flag
        this.needsRefresh = false;

        // Derive standard IDs from searcherName if provided
        const searcherName = config.searcherName;
        this.searcherName = searcherName;

        // Display options
        this.showAds = config.showAds ?? false;

        // Map layer - defaults to searcherName
        this.mapLayerName = config.mapLayerName ?? searcherName;

        // Action bar options
        this.enableCornerDelete = config.enableCornerDelete ?? false;
        this.enableActionBarDelete = config.enableActionBarDelete ?? false;
        this.enableActionBarEdit = config.enableActionBarEdit ?? false;
        this.enableActionBarRead = config.enableActionBarRead ?? false;
        this.enableActionBarSave = config.enableActionBarSave ?? false;
        this.enableActionBarClipboard = config.enableActionBarClipboard ?? true;
        this.enableActionBarLaunch = config.enableActionBarLaunch ?? true;
        this.enableActionBarAnchor = config.enableActionBarAnchor ?? true;
        this.enableRunSearchButton = config.enableRunSearchButton ?? false;
        this.alwaysShowExtraPcatInfo = config.alwaysShowExtraPcatInfo ?? false;

        // DOM element IDs - derive from searcherName if not explicitly provided
        this.containerId = config.containerId ?? (searcherName ? `${searcherName}-lister` : null);
        this.inputId = config.inputId ?? (searcherName ? `${searcherName}-search-input` : null);
        this.clearButtonId = config.clearButtonId ?? (searcherName ? `${searcherName}-search-clear` : null);

        this.supportsAnchorSort = config.supportsAnchorSort ?? false;
        this.naturalSortCache = null;

        // Storage options
        this.maxItems = config.maxItems || null,      // null = unlimited
        this.addNewToTop = config.addNewToTop !== undefined ? config.addNewToTop : true,
        this.clearAllConfirmMessage = config.clearAllConfirmMessage || 'Are you sure you want to clear all items? This cannot be undone.',


        //
        this.supportsImportLeadsFromPdf = config.supportsImportLeadsFromPdf || false,

        // for passing to layer create
        this.zIndexLayer = config.zIndexLayer ?? MAP_CONSTANTS.DEFAULT_LAYER_ZINDEX;

        // Storage key for persistence (null = no persistence)
        this.relStorageKey = config.relStorageKey || null;

        // Initialize Lister with merged options
        const rmOptions = {
            emptyMessage: config.emptyMessage,
            style: config.searchType,
            // Override with passed in options
            ...config.listerOptions
        };

        this.lister = new Lister(
            this,
            this.containerId,
            rmOptions
        );

        // Setup input handlers
        if (this.inputId) {
            this.setupInputHandlers();
        }


        // Initialize action dispatcher for click handling
        this.actionDispatcher = new ActionDispatcher(mapApp);
        this.actionDispatcher
            .register(DEFAULT_ACTION_SELECTORS.DELETE, 'actionOnItemDelete')
            .register(DEFAULT_ACTION_SELECTORS.EXECUTE_SEARCH, 'actionOnItemLaunchSearch');


        //
        this.sortOverride = null;
        }


    // ==========================================
    // SETUP
    // ==========================================

    /**
     * Hook called after state has been loaded from StateManager.
     * Subclasses may override to initialize state-dependent behavior.
     */
    initAfterStateLoaded() {
        // subclasses may derive
    }

    /**
     * Set up input event handlers using InputHandlerFactory.
     * Can be overridden for different input types (e.g., select vs text).
     */
    setupInputHandlers() {
        const input = document.getElementById(this.inputId);
        const clearButton = document.getElementById(this.clearButtonId);

        if (input) {
            // Store the cleanup function: destroy() calls it, since the input is static page DOM
            this._inputHandlerCleanup = setupTextSearchInput(
                input,
                clearButton,
                (value, isEnterKey) => this.performSearch({query:value, queryOriginal: value, isEnterKey: isEnterKey, flagUserInitiatedThisSearch:true})
            );
        }
    }



    // ==========================================
    // Lifecycle
    // ==========================================

    /**
     * Destroys the searcher and cleans up resources.
     * Searchers are rebuilt on every world load while their search inputs and checkboxes are
     * static page DOM, so every listener a searcher put on that DOM must be removed here, or each
     * world switch stacks another copy (N switches = N+1 searches per keystroke).
     * Safe to call more than once.
     */
    destroy() {
        if (this._inputHandlerCleanup) {
            this._inputHandlerCleanup();
            this._inputHandlerCleanup = null;
        }
        this.lister.destroy();
    }




    // ==========================================
    // Accessors
    // ==========================================

    /**
     * Sets the deferred refresh flag for this searcher.
     * @param {boolean} val - Whether a refresh is needed
     */
    setNeedsRefresh(val) {
        this.needsRefresh = val;
        if (val) {
            if (DEBUG_REFRESH) {
                console.log(`... searcher ${this.searcherName} recording a deferred request to refresh.`)
            }
        }
    }
    /**
     * Gets whether this searcher has a deferred refresh pending.
     * @returns {boolean} True if a refresh is needed
     */
    getNeedsRefresh() {
        return this.needsRefresh;
    }

    /**
     * Updates the showExtraPcatInfo flag based on global setting or per-searcher override.
     */
    updateShowExtraPcatInfo() {
        this.showExtraPcatInfo = this.alwaysShowExtraPcatInfo || this.getMapApp().getShowExtraPcatInfo();
    }
    /**
     * Gets whether extra place category info should be shown in results.
     * @returns {boolean} True if extra pcat info should be displayed
     */
    getShowExtraPcatInfo() {
        return this.showExtraPcatInfo;
    }

    /**
     * Gets the MapApp instance.
     * @returns {Object} The MapApp instance
     */
    getMapApp() {
        return this.mapApp;
    }

    /**
     * Gets the StateManager instance for localStorage persistence.
     * @returns {Object} The StateManager instance
     */
    getStateManager() {
        return this.mapApp.getStateManager();
    }

    /**
     * Gets the AuxDataManager instance for auxiliary data access.
     * @returns {Object} The AuxDataManager instance
     */
    getAuxDataManager() {
        return this.mapApp.getAuxDataManager();
    }

    /**
     * Gets the PoiDataManager instance for POI search methods.
     * @returns {Object} The PoiDataManager instance
     */
    getPoiDataManager() {
        return this.mapApp.getPoiDataManager();
    }

    /**
     * Gets the Lister instance.
     * @returns {Lister} the lister owned by this searcher
     */
    getLister() {
        return this.lister;
    }

    /**
     * Gets the internal results array from the lister.
     * @returns {Array} Array of result items
     */
    getListerResults() {
        return this.lister.getInternalResults();
    }

    /**
     * Sets the internal results array from the lister.
     */
    setListerResults(results) {
        return this.lister.setInternalResults(results);
    }

    /**
     * Gets another searcher by name via mapApp.
     * @param {string} name - Searcher name
     * @returns {Searcher|null} The searcher or null
     */
    getSearcherByName(name) {
        return this.mapApp.getSearcherByName(name);
    }


    /**
     * Set the input value programmatically.
     * @param {string} value - The value to set
     */
    setInputValue(value) {
        const input = document.getElementById(this.inputId);
        if (input) {
            input.value = value;
        }
    }

    /**
     * Gets the current value of the search input element.
     * @returns {string} The input value
     */
    getInputValue() {
        const input = document.getElementById(this.inputId);
        return input.value;
    }

    
    /**
     * Tell our lister to select a result by uid.
     * @param {string} uid - The unique identifier
     * @param {boolean} [flagScrollIntoView] - Whether to scroll into view
     * @param {boolean} [fromMapMarkerClick] - True if from map marker click
     * @param {boolean} [flagFlashMarker] - Whether to flash the map marker
     * @returns {boolean} True if found and selected
     */
    setSelectedResultOnOurListerByUid(uid, flagScrollIntoView, fromMapMarkerClick, flagFlashMarker, flagUserInitiated) {
        const flagSmoothAnimate = fromMapMarkerClick;
        return this.lister.setSelectedResultOnListerByUid(uid, flagScrollIntoView, fromMapMarkerClick, flagSmoothAnimate, flagFlashMarker, flagUserInitiated);
    }


    /**
     * Just proxy to lister a question about ther the uid is in the lister
     * @param {string} uid - The unique identifier
     * @returns {boolean} True if found
     */
    hasResultItemOnListerByUid(uid) {
        return this.lister.hasResultItemOnListerByUid(uid);
    }

    /**
     * Gets the OpenLayersMapManager instance.
     * @returns {Object} The map manager instance
     */
    getMapManager() {
        return this.mapApp.getMapManager();
    }

    /**
     * Returns whether this is a database searcher (DbSearcher) or internal searcher (IntSearcher).
     * @returns {boolean} True if this is a DbSearcher subclass
     * @abstract
     */
    getIsDbSearcher() {
        throw new Error('Subclass must define getIsDbSearcher.')
    }


    /**
     * Shows a loading indicator in the lister.
     * @returns {Promise<void>}
     */
    async showLoading() {
        await this.lister.showLoading();
    }


    /**
     * Gets the field name used for timestamps on items in this searcher.
     * @returns {string|null} The timestamp field name, or null if not configured
     */
    getTimeStampField() {
        return this.timeStampField;
    }


    setSortOverride(val) {
        this.sortOverride = val;
    }
    clearSortOverride() {
        this.sortOverride = null;
    }
    getSortOverride() {
        return this.sortOverride;
    }


    getName() {
        return this.searcherName;
    }

    getSupportsImportLeadsFromPdf() {
        return this.supportsImportLeadsFromPdf;
    }


    // ==========================================
    // Abstract Methods (Must Override)
    // ==========================================

    /**
     * Checks if this searcher allows POI-type results.
     * @returns {boolean} True if POI results are allowed
     * @abstract
     */
    doesAllowResultTypePoi() {
        throw new Error('Subclass must implement doesAllowResultTypePoi()');
    }

    /**
     * Checks if this searcher allows search-type results.
     * @returns {boolean} True if search results are allowed
     * @abstract
     */
    doesAllowResultTypeSearch() {
        throw new Error('Subclass must implement doesAllowResultTypeSearch()');
    }

    /**
     * Gets filtered results appropriate for this searcher type.
     * @returns {Array} Array of result items
     * @abstract
     */
    getInternalFilteredResultsIfAppropriate() {
        throw new Error('Subclass must implement getInternalFilteredResultsIfAppropriate()');
    }

    /**
     * Performs the actual search/filter work.
     * @param {Object} searchPack - The search pack with query and options
     * @abstract
     */
    async performSearchWork(searchPack) {
        throw new Error('Subclass must implement performSearchWork()');
    }

    /**
     * Performs operations after search completes.
     * @param {Object} searchPack - The search pack with query and options
     * @abstract
     */
    performPostSearchOperations(searchPack) {
        // subclass can implement
    }





    /**
     * Clears delayed search save trigger.
     * @abstract
     */
    clearDelayedLiveSearchSaveTrigger() {
        throw new Error('Subclass must implement clearDelayedLiveSearchSaveTrigger()');
    }


    /**
     * Gets the search pack from the most recent search operation.
     * @returns {Object|null} The last search pack, or null if no search has been performed
     */
    getLastSearchPack() {
        return this.lister.getLastSearchPack();
    }


    /**
     * Gets a result item from the lister by its unique identifier.
     * @param {string} uid - The unique identifier of the item
     * @returns {Object|null} The result item, or null if not found
     */
    getListerResultById(uid) {
        return this.lister.getResultById(uid)
    }

    // ==========================================
    // Virtual Methods (Can Override)
    // ==========================================

    /**
     * Hook called after search results are updated.
     * @param {Object} searchPack - The search pack with query and options
     * @param {Array} entries - The result entries
     */
    onSearchComplete(searchPack, entries) {
        // Override in subclasses if needed
    }

    /**
     * Hook called when search is cleared.
     */
    onSearchCleared() {
        // Override in subclasses if needed
        this.mapApp.displayNeedsRefresh_AfterSearchCleared(this);
    }




    /**
     * Sorts the results within a search result object in-place.
     * @param {Object} searchResult - The search result object containing a results array
     */
    doSortSearchResult(searchResult) {
        searchResult.results = this.sortResults(searchResult.results);
    }

    /**
     * Sorts results before display.
     * Override in subclasses to provide custom sorting.
     * @param {Array} results - The results to sort
     * @returns {Array} The sorted results
     */
    sortResults(results) {
        return results;
    }

    /**
     * Helper to sort results by a specified field name.
     * Uses special sorting for address fields and numeric sorting for sort_ prefixed fields.
     * @param {Array} results - The results to sort
     * @param {string} fieldName - The field name to sort by
     * @param {string} [fieldNameFallback] - Fallback field name if primary field is not present on items
     * @param {boolean} [ascending=true] - Sort direction
     * @returns {Array} The sorted results
     */
    sortResultsByField(results, fieldName, fieldNameFallback, ascending=true) {
        if (!results || !results.length) {
            return results;
        }

        // field name fallback
        if (!results[0].hasOwnProperty(fieldName) && fieldNameFallback) {
            fieldName = fieldNameFallback;
            console.error(`FALLING BACK to ${fieldName}.`);
        }

        let sortedItems;
        if (fieldName == 'address') {
            // address handled specially
            sortedItems = sortDataByFieldAddressSpecial(results);
        } else {
            // if we are sorting on a special sort_ field, we ASSUME its numeric for speed
            const flagPureNumeric = fieldName.startsWith('sort_');
            sortedItems = sortDataByField(results, fieldName, flagPureNumeric);
        }
       return sortedItems;


        // OLD CODE
        /*
        const direction = ascending ? 1 : -1;
        return results.slice().sort((a, b) => {
            const aVal = a[fieldName];
            const bVal = b[fieldName];
            // Handle undefined/null - sort them to the end
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            // Compare values
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
        });
        */
    }





    // ==========================================
    // Common Click Handlers Shared by ALL Searchers
    // ==========================================

    /**
     * Handles item click events for Internal Searchers
     * Uses ActionDispatcher for button handling and supports doubleclick actions.
     * @param {Object} item - The clicked item
     * @param {Event} e - The click event
     * @param {boolean} [flagDoubleClick] - True if this was a doubleclick
     * @override
     */
    handleItemClick(lister, item, e, flagDoubleClick) {
        // Use action dispatcher for registered button handlers (i think the only purpose here is to detect a click on an items (special action) BUTTON)
        const uid = item.uid;

        //
        if (this.actionDispatcher.dispatch(e, item, this.lister)) {
            return;
        }

        // Handle doubleclick on saved search items
        if (flagDoubleClick) {
            // DOUBLE CLICK
            if (item.ptype === PTYPES.SEARCH) {
                // re-run the search programmatircally
                this.mapApp.actionOnItemLaunchSearch(uid, item, this.lister);
                return;
            } else if (item.ptype === PTYPES.PDFLEAD) {
                // re-run the search programmatircally
                this.mapApp.actionOnItemReadLead(uid, item, this.lister);
                return;
            } else {
                // should we ZOOM TO a poi item?
                // ATTN: I think when we do this we want to force expand (select) it if its not expanded (though im not sure this can happen)
                // this might happen if user double-clicks on an already opened item; it might have been toggled close first by the single click
                // most of the time i think this will do nothing
                lister.expandResultByUid(uid, true);
                // now "launch" it, which should basically just JUMP to it
                this.mapApp.actionOnItemLaunchItem(uid, item, this.lister);
            }
        } else {
            // SINGLE CLICK
            // Toggle clicked item's selection (don't scroll since user just clicked it)
            // only toggle on SINGLE click since doubleclick is proceeded by one and we dont want to toggle it
            lister.toggleSelectedResultByUid(uid);
        }


    }



    // ==========================================
    // Input Handlers
    // ==========================================



    /**
     * Updates the visibility of the clear button based on input value.
     */
    updateClearButtonVisibility() {
        const select = document.getElementById(this.inputId);
        const clearButton = document.getElementById(this.clearButtonId);
        updateElementVisibilityBasedOnInputElementValue(clearButton, select);
    }


    


    // ==========================================
    // Search Operations
    // ==========================================

    /**
     * Main search entry point.
     * Delegates to performSearchWork() which is implemented by subclasses.
     * Handles pre-search operations, post-search sorting, and selection restoration.
     * @param {Object} searchPack - The search pack with query, isEnterKey, flagUserInitiatedThisSearch, etc.
     */
    // lets try making this async
    async performSearch(searchPack) {

        // clear this flag
        this.setNeedsRefresh(false);

        // Save currently selected item's uid before re-rendering
        const previouslySelectedUid = this.lister.getSelectedResultUid();

        // set searchType if not set, and queryOriginal
        searchPack.searchType ??= this.searchType;
        searchPack.queryOriginal ??= searchPack.query;

        // start of search
        this.performPreStartSearchOperations();
        
        // Delegate to subclass implementation
        await this.performSearchWork(searchPack);

        // Call hook for subclass-specific behavior
        this.onSearchComplete(searchPack, this.lister.getInternalResults());


        if (DEBUG_CBM) {
            console.debug(`DEBUG ATTN: Reporting from searcher ${this.searcherName}; back from search with "${JSON.stringify(searchPack)}".`);
        }

        // Post-search operations
        this.performPostSearchOperations(searchPack);
        this.performPostSearchSorting();

        // Restore selection state if the previously selected item still exists
        if (previouslySelectedUid) {
            // ATTN: we are just restoring so no smooth scrolling, but yes it should be in view
            const flagScrollIntoView = true;
            const fromMapMarkerClick = false;
            const flagFlashMarker = false;
            const flagUserInitiated = false;
            this.setSelectedResultOnOurListerByUid(previouslySelectedUid, flagScrollIntoView, fromMapMarkerClick, flagFlashMarker, flagUserInitiated);
        }

    }







    /**
     * Performs operations before starting a search.
     * @protected
     */
    performPreStartSearchOperations() {
        this.clearDelayedLiveSearchSaveTrigger();
        this.updateClearButtonVisibility();
        clearQueuedDelayedSearchMessages();
    }




    // ==========================================
    // Result Refresh
    // ==========================================

    /**
     * Re-performs search to fully refresh result display.
     * @param {boolean} flagUserInitiatedThisSearch - Whether to consider updating search history
     */
    rePerformSearchToFullyRefreshResultDisplay(flagUserInitiatedThisSearch) {
        if (DEBUG_REFRESH) {
            console.log(`... searcher ${this.searcherName} performing full refresh.`)
        }
        const inputElement = document.getElementById(this.inputId);
        const query = inputElement?.value ?? '';
        const searchPack = {
            query: query,
            queryOriginal: query,
            isEnterKey: true,
            flagUserInitiatedThisSearch: flagUserInitiatedThisSearch,
        };
        this.performSearch(searchPack);
    }

    /**
     * Updates appearance of an item by uid (e.g., bookmark icon).
     * @param {string} uid - The item uid
     */
    updatePartialAppearanceOfItemByUid(uid) {
        this.lister.updateAppearanceOfItemActionBarByUidOnLister(uid);
    }


    /**
     * Asks user for confirmation then clears all results.
     * Can be overridden by derived class
     */
    confirmThenClearAllResults() {
        // Defer confirm() so Bootstrap dropdown can close first;
        // a synchronous confirm() blocks the event loop and prevents
        // the dropdown from dismissing, which can cause the dialog
        // to reappear when focus returns.
        setTimeout(() => {
            if (confirm(this.clearAllConfirmMessage)) {
                this.lister.clearResultList();
                // save state if appropriate
                this.saveResultsToStorage();
                // refresh search
                this.performClearSearch(true);
            }
        }, 0);
    }


    /**
     * Performs a search with an empty query, effectively clearing results.
     * @param {boolean} flagClearSearchInputs - Whether to also clear the search input elements
     */
    performClearSearch(flagClearSearchInputs) {
        if (flagClearSearchInputs) {
            // ATTN: 
            this.clearAllSearchInputs();
        }
        const searchPack = {
            query: '',
            queryOriginal: '',
            isEnterKey: true
        }
        this.performSearch(searchPack);
    }


    // ==========================================
    // Map Layer Operations
    // ==========================================

    /**
     * Update map result layer with search/filter results.
     * @param {Array} results - Results to display on map
     */
    updateMapResultLayer(results) {
        if (!this.mapApp || !this.mapApp.mapManager) {
            console.error('ERROR: Map not available for updating result layer');
            return;
        }
        if (!this.mapLayerName) {
            return;
        }

        const layerName = this.mapLayerName;
        try {
            this.mapApp.appUpdateMapFeatureMarkersLayerWithNewResults(layerName, results);
        } catch (error) {
            console.error(`Error: Failed to update map layer "${layerName}":`, error);
        }
    }

    /**
     * Clear map result layer.
     */
    clearMapResultLayer() {
        if (!this.mapApp || !this.mapApp.mapManager) {
            return;
        }
        if (this.mapLayerName) {
            this.mapApp.mapManager.clearResultLayer(this.mapLayerName);
        }
    }




    // ==========================================
    // Static Utilities
    // ==========================================

    /**
     * Create HTML for a positioned delete button.
     * @param {string} itemUId - The item ID for data attribute
     * @param {string} [title='Remove'] - Tooltip title
     * @param {string} [icon='bi-x-square'] - Bootstrap icon class
     * @returns {string} HTML string for the button
     */
    static createDeleteButtonPositioned(itemUId, title = 'Remove', icon = 'bi-x-square') {
        return HtmlBuilder.deleteButtonPositioned(itemUId, title, icon);
    }




    // ==================== 
    // CAN BE SUBLASSED

    /**
     * Clears all search input elements. Can be overridden by subclasses
     * that have additional input elements beyond the main search input.
     */
    clearAllSearchInputs() {
        // clear the search
        this.setInputValue("");
    }
    // ====================








    // ==================== 
    // NEW SEARCH BACK STUFF
    // ==================== 

    /**
     * Sets input element values from a search pack object.
     * Derived classes may override to set additional inputs (e.g., region select).
     * @param {Object} searchPack - The search pack containing query and options
     */
    setInputValuesFromSearchPack(searchPack) {
        // set input value (and possible derived classes could set other things from searchPack)
        this.setInputValue(searchPack.query);
    }



    /**
     * Re-runs a search from an existing search pack.
     * Triggered when user clicks "show all" or "re-randomize" link.
     * Resets the search pack to its original state and sets limit to show all (unless random).
     * IntSearcher overrides this to simply clear and re-run.
     * @param {Object} searchPack - The search pack to re-run
     */
    reRunSearchPack(searchPack) {
        // Re-Run a search (this is triggered from user clicking on "show-all" or "re-randomize" link); NOTE that IntSearcher overrides this

        // reset original search which may be normalized during searching
        resetSearchPackToOriginalBlankSearch(searchPack);
        //
        // if its random, dont change limit, but if not, set limit to all
        if (!searchPack.random) {
            searchPack.overrideLimit = RESULT_MANAGER_CONSTANTS.NO_LIMIT_RESULT_COUNT;
        }
        // now run it
        this.performSearch(searchPack);
    }


    /**
     * Converts the current search into an advanced search query and runs it.
     * Subclasses should override to build the appropriate advanced query expression.
     */
    makeAdvancedSearch() {
        const searchQuery = 'test me';
        const searchType = SEARCH_TYPES.ADVANCED;
        this.mapApp.programmaticallyRunSearchOnQuery(searchType, searchQuery, false);
    }


    /**
     * Returns whether this searcher's results depend on the currently selected region.
     * Subclasses that filter by region should override to return true.
     * @returns {boolean} True if search depends on selected region
     */
    doesSearchDependOnSelectedRegion() {
        return false;
    }



    // ==========================================
    // ADDING NEW RESULT ITEMS
    // ==========================================

    // Note that these functions for adding results are NOT used by the DbSearcher, which sets results in bulk
    // They are used instead by the Internal Searchers, which keep track of bookmarks, history, savedsearches, etc.

    /**
     * Add an independent copy of the item to the list.
     * @param {Object} sourceItem - item to copy and add
     * @param {string} debugComment - Shown to console on add
     * @param {boolean} flagResetTimestamp - Whether to reset the timestamp
     * @returns {Object|null} The added item or null
     */
    addResultCopy(sourceItem, debugComment, flagResetTimestamp) {
        return this._addResultCopyOrRef(true, sourceItem, debugComment, flagResetTimestamp);
    }

    /**
     * Add the item directly as a reference (no copy).
     * @param {Object} sourceItem - item to add by reference
     * @param {string} debugComment - Shown to console on add
     * @param {boolean} flagResetTimestamp - Whether to reset the timestamp
     * @returns {Object|null} The added item or null
     */
    addResultRef(sourceItem, debugComment, flagResetTimestamp) {
        return this._addResultCopyOrRef(false, sourceItem, debugComment, flagResetTimestamp);
    }


    /**
     * Add an item to the list, either as an independent copy or as a direct reference.
     * Called by things like search history, bookmarks, and import.
     * @param {boolean} flagCreateCopy - If true, creates an independent copy of the item; if false, stores the item directly as a reference
     * @param {Object} sourceItem - item to add
     * @param {string} debugComment - Shown to console on add
     * @param {boolean} flagResetTimestamp - Whether to reset the timestamp
     * @returns {Object|null} The added item or null
     */
    _addResultCopyOrRef(flagCreateCopy, sourceItem, debugComment, flagResetTimestamp) {
        if (!sourceItem || !sourceItem.uid) {
            console.error(`ERROR: Cannot add invalid item (${debugComment}) (missing uid)`);
            return null;
        }

        const lister = this.lister;

        // Remove old duplicates before adding new
        // we can only do this compare if they are search based items
        if (sourceItem.ptype == PTYPES.SEARCH) {
            const flagRequireDataMatch = true;
            const itemOld = lister.findResultWithDuplicateContent(sourceItem, flagRequireDataMatch);
            if (itemOld) {
                // we are just removing an old item dont do any data fixing of poi stuff
                this.removeItemByUidItem(itemOld.uid, itemOld, false);
            }
        }

        // always remove duplicate uid
        const itemOld = lister.getResultById(sourceItem.uid);
        if (itemOld) {
            // we are just removing an old item dont do any data fixing of poi stuff
            this.removeItemByUidItem(itemOld.uid, itemOld, false);
        }

        // Create item copy or use directly as reference
        const item = flagCreateCopy ? createItemCopy(sourceItem) : sourceItem;

        // give subclasses a chance to add to it (like bookmark field)
        this.processItemBeforeAdd(item);

        // modify to add/reset timestamp?
        const timeStampField = this.getTimeStampField();
        if (flagResetTimestamp || !item[timeStampField]) {
            // set timestamp to now
            item[timeStampField] = Date.now();
        }

        // Add to list
        const results = lister.getInternalResults();
        if (this.addNewToTop) {
            results.unshift(item);
        } else {
            results.push(item);
        }

        // Trim list if it exceeds max items
        if (this.maxItems && results.length > this.maxItems) {
            lister.setInternalResults(results.slice(0, this.maxItems));
        }

        if (DEBUG_CBM) {
            console.debug(`DEBUG: addResultCopyOrRef (${debugComment}): "${JSON.stringify(item)}"`);
        }

        return item;
    }




    /**
     * Hook to process an item before it is added to results.
     * Subclasses can override to set additional properties (e.g., isBookmark flag).
     * @param {Object} item - The item about to be added
     */
    processItemBeforeAdd(item) {
        // base class does nothing
    }




    /**
     * Rebuilds and re-displays the result list without necessarily re-running a database search.
     * By default re-runs the search; DbSearcher overrides to just re-render existing results.
     */
    reBuildReDisplayResultList() {
        // rebuild the results but no strict need to re-run a db search
        // by default just re-run search, but DB searchers should avoid doing so
        this.rePerformSearchToFullyRefreshResultDisplay(false);
    }



















   

    // ==========================================
    // STATE MANAGEMENT
    // ==========================================




    /**
     * Loads results from localStorage using the configured storage key.
     * Sets results on the lister.
     */
    loadResultsFromStorage() {
        if (!this.relStorageKey) {
            this.lister.loaded = true;
            return;
        }

        const stateManager = this.getStateManager();
        const fullStorageKey = stateManager.addFullStoragePrefix(this.relStorageKey)
        try {
            const storedDataJsonString = localStorage.getItem(fullStorageKey);
            if (storedDataJsonString && storedDataJsonString!="undefined" && storedDataJsonString!== undefined) {
                const serializedData = JSON.parse(storedDataJsonString);
                this.lister.setInternalResults(this.unSerializeData(serializedData));
            } else {
                this.lister.setInternalResults([]);
            }
            this.lister.loaded = true;
        } catch (error) {
            console.error(`ERROR: Failed to load results from storage (${fullStorageKey}):`, error);
            this.lister.setInternalResults([]);
            this.lister.loaded = true;
        }
    }

    /**
     * Saves results to localStorage using the configured storage key.
     */
    saveResultsToStorage() {
        if (!this.relStorageKey) return;

        const stateManager = this.getStateManager();
        const fullStorageKey = stateManager.addFullStoragePrefix(this.relStorageKey)
        try {
            const serializedData = this.serializeData();
            localStorage.setItem(fullStorageKey, JSON.stringify(serializedData));
        } catch (error) {
            console.error(`ERROR: Failed to save results to storage (${fullStorageKey}):`, error);
        }
    }


    /**
     * Serializes the current results for localStorage persistence.
     * Subclasses must implement this if they use storage.
     * @returns {Array|null} Serialized data array, or null if no storage key configured
     * @abstract
     */
    serializeData() {
        // sublcasses must define
        if (!this.relStorageKey) return null;
        throw new Error(`${this.constructor.name}.serializeData: subclasses must implement this.`);
    }

    /**
     * Deserializes stored data back into result items.
     * Subclasses must implement this to reconstruct items from stubs.
     * @param {Array} serializedData - The serialized data from localStorage
     * @returns {Array} Array of deserialized result items
     * @abstract
     */
    unSerializeData(serializedData) {
        // subclasses must implement this and return the deserialized results array
        throw new Error(`${this.constructor.name}.unSerializeData: subclasses must implement this.`);
    }















    // ==========================================
    // SORTING
    // ==========================================





    /**
     * Performs post-search sorting operations. Clears natural sort cache
     * and applies custom sorting (e.g., anchor distance) if appropriate.
     * @private
     */
    performPostSearchSorting() {
        this.naturalSortCache = null;
        const didSort = this.postSearchDoCustomSortingIfAppropriate();
        return didSort;
    }


    /**
     * Applies custom sorting after a search if conditions are met.
     * Currently supports sorting by anchor distance for searchers that opt in.
     * @private
     */
    postSearchDoCustomSortingIfAppropriate() {
        if (this.supportsAnchorSort) {
            const shouldSortByAnchorDist = this.getMapApp().calcShouldSortByAnchorDist();
            if (shouldSortByAnchorDist) {
                this.sortResultsByAnchorDistance();
                return true;
            }
        }

        return false;
    }





    /**
     * Sorts results by distance to the current anchor point.
     * Items with smaller distance appear first; items without geometry go to the end.
     * Caches the natural sort order so it can be restored later.
     */
    sortResultsByAnchorDistance() {
        // sort results by distance to anchor, with smaller distance at top and items with no geometry at bottom
        // BEFORE sorting ensure that we have cached 
        const results = this.getListerResults();
        if (results.length==0) {
            return;
        }

        // save original order so we can return back to it
        if (!this.naturalSortCache) {
            this.saveNaturalSortCache();
        }

        // sort results by distance to anchor
        const anchor = this.getMapApp().getCurrentAnchor();
        const anchorItem = anchor.item;
        results.sort((a, b) => {
            const distA = distanceBetweenTwoItems(a, anchorItem);
            const distB = distanceBetweenTwoItems(b, anchorItem);
            // items without geometry go to the end
            if (distA === null && distB === null) return 0;
            if (distA === null) return 1;
            if (distB === null) return -1;
            return distA - distB;
        });

        // now redisplay
        this.redisplaySortedResults();
    }
    

    /**
     * Saves the current result order as the natural sort cache.
     * Used to restore original ordering after anchor-distance sorting.
     * @private
     */
    saveNaturalSortCache() {
        const results = this.getListerResults();
        this.naturalSortCache = results.map(item => item.uid);
    }

    /**
     * Restores the result order from the natural sort cache.
     * Clears the cache after restoring.
     * @private
     */
    restoreFromNaturalSortCache() {
        const results = this.getListerResults();
        const orderMap = new Map(this.naturalSortCache.map((uid, index) => [uid, index]));
        results.sort((a, b) => {
            const indexA = orderMap.get(a.uid) ?? Number.MAX_SAFE_INTEGER;
            const indexB = orderMap.get(b.uid) ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
        this.naturalSortCache = null;
    }
    
    /**
     * Restores results to their default (natural) sort order.
     * If a natural sort cache exists, restores from it; then re-displays results.
     */
    sortResultsByDefault() {
        // sort results by naturalSort value which
        const results = this.getListerResults();
        if (results.length==0) {
            return;
        }
        if (this.naturalSortCache) {
            this.restoreFromNaturalSortCache();
        }

        // now redisplay
        this.redisplaySortedResults();
    }


    /**
     * Re-renders the result list after sorting, preserving the current selection state.
     * @private
     */
    redisplaySortedResults() {
        // Save currently selected item's uid before re-rendering
        const previouslySelectedUid = this.lister.getSelectedResultUid();

        // render results
        this.reBuildReDisplayResultList();

        // Restore selection state if the previously selected item still exists
        if (previouslySelectedUid) {
            // ATTN: we are just restoring so no smooth scrolling, but yes it should be in view
            const flagScrollIntoView = true;
            const fromMapMarkerClick = false;
            const flagFlashMarker = false;
            const flagUserInitiated = false;
            this.setSelectedResultOnOurListerByUid(previouslySelectedUid, flagScrollIntoView, fromMapMarkerClick, flagFlashMarker, flagUserInitiated);
        }
    }







    /**
     * Hook for potential auto-zoom/pan after a search completes with Enter key.
     * Currently a no-op; could be enabled to fit all search results within extent.
     */
    maybeZoomPanOnEnterFinishedSearch() {
        // we COULD try to ensure all visible items are within extent when we get full search results, but probably not
        // this.mapApp.autoZoomAllVisibleFeatureExtents(AUTO_ZOOM_STYLE.EXPAND);
    }



    clearCurrentSearcherSelection() {
        const lister = this.getLister();
        lister.clearSelectedResult();
    }

}


